import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Button,
  ButtonProps,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  Link,
  TextField,
  styled
} from "@mui/material";
import { Box } from "@mui/system";
import {
  Consol20Px,
  Checkmark24Px,
  basicColours,
  useToast,
  AlertBanner
} from "@qubit/autoparts";
import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";

import { useDispatch } from "react-redux";

import { useAppSelector } from "~/app/store";
import TestingToolLayout from "~/features/testingTools/TestingToolLayout";

import {
  addEvent,
  resetPtlTestSession,
  selectActivePtlPhaseDetails,
  selectPtlPhaseStatuses,
  setPhaseStatus,
  setPtlPhaseNotification,
  transitionToPtlPhase
} from "~/features/testingTools/testingTools.slice";
import { TestingTool } from "~/features/testingTools/testingTools.types";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { runAsyncSafely } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUserCanPick } from "~/redux/selectors/authSelectors";
import {
  selectThisWorkstation,
  selectWorkstationHasPickingRole
} from "~/redux/selectors/workstationsSelectors";

import { useStartPtlTestMutation } from "~/redux/warehouse/testingTools.hook";

import { WorkstationSummaryDto } from "~/types/api";

import WorkstationCellGrid from "./WorkstationCellGrid";

const PTLTestWorkflowContainer = styled(Box)(({ theme: { spacing } }) => ({
  display: "grid",
  height: "100%",
  width: "100%",
  gridTemplateRows: "100px 1fr minmax(100px, auto)",
  rowGap: spacing(2),
  justifyContent: "center"
}));

export const PTLUserNotificationList = (props: {
  messagesList: ReactNode[];
  messageTitle?: string;
}) => {
  const { messagesList, messageTitle } = props;
  return (
    <List
      subheader={
        messageTitle ? (
          <Typography variant="h6" aria-label="notification list title">
            {messageTitle}
          </Typography>
        ) : undefined
      }
      disablePadding
    >
      {messagesList.map((message, phaseIndex) => {
        return (
          <ListItem disablePadding key={`ptl-user-notification-${phaseIndex}`}>
            <ListItemIcon>
              <FiberManualRecordIcon
                sx={{
                  width: ".75rem",
                  height: ".75rem",
                  color: basicColours.mono.black
                }}
              />
            </ListItemIcon>
            {typeof message === "string" ? (
              <Typography variant="subtitle1" key={phaseIndex}>
                {message}
              </Typography>
            ) : (
              message
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

const PTLTestWorkflow = ({
  onTestingSessionStart
}: {
  onTestingSessionStart: (testType: TestingTool) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { errorToast } = useToast();
  const navigate = useFcNavigate();

  const selectedWs = useAppSelector(selectThisWorkstation);
  const canUserPick = useAppSelector(selectUserCanPick);
  const isPickingRoleFound = useAppSelector(selectWorkstationHasPickingRole);
  const hasPickingRole = canUserPick && isPickingRoleFound;
  const activePhase = useAppSelector(selectActivePtlPhaseDetails);
  const ptlTestingSteps = useAppSelector(selectPtlPhaseStatuses);
  const hasPingVerificationPhaseMounted = useRef(false);
  const isPingVerificationPhaseAwaitingFirstPing = useMemo(() => {
    if (
      !activePhase.deviceStatusById ||
      activePhase.phaseIndex !== "2" ||
      activePhase.phase !== "ping_verification" ||
      activePhase.status !== "running"
    )
      return false;
    return Object.values(activePhase.deviceStatusById).every(
      (ptlStatus) => ptlStatus.status === null
    );
  }, [activePhase]);

  const [startPtlTest] = useStartPtlTestMutation();

  const clearPTLDisplays = useCallback<
    (ws: WorkstationSummaryDto) => Promise<void>
  >(
    async (ws: WorkstationSummaryDto) => {
      await runAsyncSafely(
        startPtlTest({
          displaysToActivate: [],
          displaysToClean: ws.totePlacements.map((tp) => tp.putToLightModuleId),
          gridId: ws.autostoreGridId,
          workstationId: ws.id
        }).unwrap(),
        {
          onError: (error) => {
            errorToast(getMessageFromRtkError(error));
          }
        }
      );
    },
    [errorToast, startPtlTest]
  );

  const onStartPTLTestCb = useCallback(async () => {
    if (!selectedWs) return;

    await runAsyncSafely(
      startPtlTest({
        displaysToActivate: selectedWs.totePlacements.map(
          (tp) => tp.putToLightModuleId
        ),
        displaysToClean: [],
        gridId: selectedWs.autostoreGridId,
        workstationId: selectedWs.id
      }).unwrap(),
      {
        onError: (error) => {
          errorToast(getMessageFromRtkError(error));
        },
        onSuccess: async () => {
          await onTestingSessionStart("PTL");
          dispatch(
            setPhaseStatus({
              phaseIndex: activePhase.phaseIndex,
              newState: "running"
            })
          );
        }
      }
    );
  }, [
    activePhase.phaseIndex,
    dispatch,
    errorToast,
    onTestingSessionStart,
    selectedWs,
    startPtlTest
  ]);

  const resetPtlSessionAndPTLDisplays = useCallback(async () => {
    if (!selectedWs) return;
    await clearPTLDisplays(selectedWs);
    dispatch(resetPtlTestSession());
  }, [clearPTLDisplays, dispatch, selectedWs]);

  useEffect(() => {
    if (!activePhase.deviceStatusById) return;

    const didAllPTLPingTestsPass = Object.values(
      activePhase.deviceStatusById
    ).every((ptlStatus) => ptlStatus.status === "success");

    if (didAllPTLPingTestsPass) {
      dispatch(
        addEvent({
          text: `PTL test: ping received for all devices`,
          state: "Info",
          testOrigin: "PTL"
        })
      );
      dispatch(
        setPhaseStatus({
          phaseIndex: "2",
          newState: "completed"
        })
      );
    }
  }, [activePhase.deviceStatusById, dispatch]);

  useEffect(() => {
    if (
      !isPingVerificationPhaseAwaitingFirstPing ||
      !selectedWs ||
      hasPingVerificationPhaseMounted.current
    )
      return;

    // If the ping verification phase is mounted and awaiting the first ping,
    // clear the PTL displays to ensure a clean state
    void (async () => {
      await clearPTLDisplays(selectedWs);
      hasPingVerificationPhaseMounted.current = true;
    })();
  }, [isPingVerificationPhaseAwaitingFirstPing, clearPTLDisplays, selectedWs]);

  useEffect(() => {
    if (selectedWs && !hasPickingRole) {
      dispatch(
        setPtlPhaseNotification({
          message: (
            <Typography variant="subtitle1">
              <span
                style={{
                  fontWeight: 500
                }}
              >
                {t("ptl test unavailable")}
              </span>
              {" - "}
              {!isPickingRoleFound && canUserPick
                ? t("no picking role found")
                : t("user has insufficient privileges")}
              .
              <br />
            </Typography>
          ),
          type: "warning"
        })
      );
    }
  }, [
    canUserPick,
    dispatch,
    hasPickingRole,
    isPickingRoleFound,
    selectedWs,
    t
  ]);

  const buttonsToDisplay = useMemo((): Array<
    ButtonProps & {
      buttonText: string;
      onClick: () => Promise<void> | void;
    }
  > => {
    if (!selectedWs) return [];

    const selectedStep = ptlTestingSteps[activePhase.phaseIndex];
    if (!selectedStep) return [];

    const defaultButtonSx = {
      minWidth: 200,
      minHeight: 50,
      py: 2,
      borderRadius: "6px",
      fontSize: "1.25rem",
      fontWeight: 500,
      mb: 3
    };
    if (selectedStep.phase === "layout_verification") {
      switch (selectedStep.status) {
        case "preview":
          return [
            {
              buttonText: t("start test"),
              startIcon: (
                <Consol20Px
                  style={{
                    fill: basicColours.mono.white,
                    width: "1.25rem",
                    height: "1.25rem",
                    position: "relative"
                  }}
                />
              ),
              variant: "contained",
              color: "primary",
              sx: {
                ...defaultButtonSx,
                minWidth: 250
              },
              onClick: onStartPTLTestCb
            }
          ];
        case "running":
          return [
            {
              buttonText: t("mismatch"),
              variant: "subtle",
              sx: defaultButtonSx,
              onClick: () => {
                dispatch(
                  addEvent({
                    text: "PTL test: On-screen position numbers do not match physical device assignment",
                    state: "Failed",
                    testOrigin: "PTL"
                  })
                );
                dispatch(
                  setPtlPhaseNotification({
                    message: (
                      <PTLUserNotificationList
                        messagesList={[
                          t("ptl device installation order error"),
                          <Typography variant="subtitle1">
                            <Trans
                              i18nKey="update workstation configuration"
                              components={[
                                <Link
                                  component="button"
                                  onClick={() =>
                                    navigate(
                                      "/settings?settings-tab=workstation-config"
                                    )
                                  }
                                  aria-label="workstation settings link"
                                  sx={{ cursor: "pointer", mx: 0.5 }}
                                  color="textPrimary"
                                />
                              ]}
                            />
                          </Typography>
                        ]}
                        messageTitle={t("mismatch options")}
                      />
                    ),
                    type: "warning"
                  })
                );
              }
            },
            {
              buttonText: t("confirm"),
              variant: "contained",
              color: "primary",
              sx: defaultButtonSx,
              onClick: () => {
                dispatch(
                  addEvent({
                    text: `PTL test: layout verification phase completed`,
                    state: "Success",
                    testOrigin: "PTL"
                  })
                );
                dispatch(
                  transitionToPtlPhase({
                    phaseIndex: "2",
                    ptlIdList: selectedWs.totePlacements.map(
                      (tp) => tp.putToLightModuleId
                    )
                  })
                );
              }
            }
          ];
      }
    } else if (selectedStep.phase === "ping_verification") {
      switch (selectedStep.status) {
        case "completed":
          return [
            {
              buttonText: t("confirm"),
              variant: "contained",
              color: "primary",
              startIcon: (
                <Checkmark24Px
                  style={{
                    fill: basicColours.mono.white,
                    width: "1.25rem",
                    height: "1.25rem"
                  }}
                />
              ),
              sx: defaultButtonSx,
              onClick: () => {
                dispatch(
                  addEvent({
                    text: "PTL Test Complete: Ping verification succeeded",
                    state: "Success",
                    testOrigin: "PTL"
                  })
                );
                void resetPtlSessionAndPTLDisplays();
              }
            }
          ];
      }
    }
    return [];
  }, [
    selectedWs,
    ptlTestingSteps,
    activePhase.phaseIndex,
    t,
    onStartPTLTestCb,
    dispatch,
    navigate,
    resetPtlSessionAndPTLDisplays
  ]);

  const userNotificationMessage = useMemo(() => {
    if (!activePhase.notification) return null;
    const { message, type } = activePhase.notification;
    return (
      <AlertBanner
        severity={type}
        sx={{
          py: typeof message === "string" ? 1 : 0,
          px: 2,
          mb: 2,
          color: "black"
        }}
        skipCustomTypography={typeof message !== "string"}
      >
        {message}
      </AlertBanner>
    );
  }, [activePhase.notification]);

  return (
    <TestingToolLayout>
      <PTLTestWorkflowContainer
        role="region"
        aria-label="Pick to light test content"
      >
        <Stack
          flexDirection={"column"}
          alignContent={"center"}
          maxWidth={600}
          mt={1}
          alignSelf={"start"}
        >
          <Typography
            variant="h6"
            sx={{ ml: 0.5 }}
            id="selected-workstation-view"
          >
            {t("workstation")}
          </Typography>
          <TextField
            type="text"
            variant="outlined"
            value={selectedWs?.deviceId || ""}
            slotProps={{
              input: {
                readOnly: true,
                "aria-labelledby": "selected-workstation-view",
                sx: { minWidth: 500, height: 40 }
              }
            }}
            sx={{
              pointerEvents: "none",
              p: 0
            }}
          />
        </Stack>
        {hasPickingRole && selectedWs ? (
          <>
            <WorkstationCellGrid selectedWorkstation={selectedWs} />
            <Stack flexDirection="column" gap={1} alignItems={"center"}>
              {userNotificationMessage}
              {activePhase.phase === "layout_verification" &&
                activePhase.status === "running" && (
                  <Typography variant="h6">
                    {t("ptl position confirmation")}
                  </Typography>
                )}
              {buttonsToDisplay.length > 0 && (
                <Stack flexDirection="row" gap={3} alignItems={"center"} mb={1}>
                  {buttonsToDisplay.map(({ buttonText, ...buttonProps }) => (
                    <Button {...buttonProps} key={`btn-${buttonText}`}>
                      {buttonText}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>
          </>
        ) : (
          <Box
            sx={{
              alignSelf: "flex-start"
            }}
          >
            {userNotificationMessage}
          </Box>
        )}
      </PTLTestWorkflowContainer>
    </TestingToolLayout>
  );
};

export default PTLTestWorkflow;
